import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useEntitlements } from "../lib/entitlements";
import LockedFeature from "../components/LockedFeature";
import { Phone, Copy, ArrowRight, CheckCircle, Warning, LinkSimple, PaperPlaneTilt, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import VoiceTour, { hasSeenVoiceTour } from "../components/VoiceTour";
import { useManagerPinGate } from "../components/ManagerPinGate";

const formatTime = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const WebhookStatusBanner = ({ business, refresh }) => {
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(null); // { webhooks_ok, live_voice_url, live_sms_url }
  const [ringing, setRinging] = useState(false);
  const numberSaved = !!business?.twilio_phone_number;
  const dbWired = !!business?.twilio_webhooks_configured_at;

  // Poll Twilio for the ACTUAL currently-configured webhook URLs. This is the
  // only way to catch the case where our DB says "wired" (twilio_webhooks_configured_at
  // is set from a previous successful wire) but Twilio's live config has since
  // been swapped, deleted, or edited manually in the console.
  useEffect(() => {
    if (!numberSaved) return;
    let cancelled = false;
    const check = async () => {
      try {
        const r = await api.get("/twilio/status");
        if (!cancelled) setLive(r.data);
      } catch (err) {
        // Transient status poll error (network blip, temporary Twilio 5xx).
        // Keep the last known good state on screen; the interval will retry.
        console.debug("twilio/status poll failed, keeping last known state:", err?.message);
      }
    };
    check();
    const t = setInterval(check, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [numberSaved]);

  const liveKnown = live !== null && live.webhook_check_error == null;
  const liveMismatch = liveKnown && live.webhooks_ok === false;
  const shouldShow = numberSaved && (!dbWired || liveMismatch);
  if (!shouldShow) return null;

  // After a successful auto-wire, place a real test call to the operator's
  // mobile so they HEAR the AI answering — turns the fix into a confirmed
  // "you're back online" moment instead of trusting a toast.
  const placeConfirmationCall = async () => {
    const to = business.owner_phone || "";
    if (!to || to.length < 4) {
      toast.info("Add your mobile to Settings → Owner phone to auto-test-call next time.");
      return;
    }
    setRinging(true);
    try {
      const r = await api.post("/twilio/test-call", { to });
      toast.success(r.data.message || `Ringing ${to} — pick up to hear your AI greeting live.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test call failed — try dialing your Twilio number manually.");
    }
    setRinging(false);
  };

  const fix = async () => {
    setBusy(true);
    try {
      const r = await api.post("/twilio/auto-configure", {});
      toast.success(`Webhooks wired — ${r.data.phone_number} is live.`);
      refresh && refresh();
      // Re-poll immediately so the banner disappears on success.
      try { const rr = await api.get("/twilio/status"); setLive(rr.data); } catch (err) {
        // Immediate re-poll after fix — non-fatal if it fails, the 30s interval will retry.
        console.debug("post-fix twilio/status re-poll failed:", err?.message);
      }
      // Fire the confirmation call — it's the fastest way for the operator
      // to know "yes, my AI actually answers now" without dialing manually.
      await placeConfirmationCall();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-wire failed — check that your Twilio creds still work.");
    }
    setBusy(false);
  };

  // Compose a human-readable "here's exactly what's wrong" body.
  let headline = "Twilio number saved, but webhooks aren't wired yet";
  let detail = `That's why calls to ${business.twilio_phone_number} ring once and drop — Twilio has no idea to send them to your AI. One click fixes it and rings your mobile so you hear the AI answer live.`;
  if (liveMismatch) {
    headline = "Twilio is pointing at the wrong URL — calls will drop instantly";
    const parts = [];
    if (live.voice_url_ok === false) {
      parts.push(`Voice URL should end in /api/twilio/voice/${business.slug} — currently: ${live.live_voice_url || "(blank)"}`);
    }
    if (live.sms_url_ok === false) {
      parts.push(`SMS URL should end in /api/twilio/inbound — currently: ${live.live_sms_url || "(blank)"}`);
    }
    detail = parts.join(" · ");
  }

  return (
    <div className="paper-card p-4 mb-6 border-2 border-[#A5382A] bg-[#FEF2F0]" data-testid="webhook-status-banner">
      <div className="flex items-start gap-3">
        <Warning size={22} color="#A5382A" weight="fill" className="flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-heading font-extrabold text-[#A5382A] text-base leading-snug">{headline}</div>
          <p className="text-sm text-[#7A1F16] leading-snug mt-1 break-words">{detail}</p>
        </div>
        <button
          onClick={fix}
          disabled={busy || ringing}
          className="btn-primary flex-shrink-0"
          data-testid="webhook-banner-fix-btn"
        >
          {busy ? "Wiring…" : ringing ? "Ringing you…" : "Fix it now"}
        </button>
      </div>
    </div>
  );
};

const CallHandlerToggle = ({ business, refresh }) => {
  const [mode, setMode] = useState(business.call_handler || "ai");
  const [fwd, setFwd] = useState(business.forward_phone || "");
  const [busy, setBusy] = useState(false);
  const gate = useManagerPinGate();

  const save = async (newMode, newFwd) => {
    // Turning AI OFF is the sabotage vector — gate it.
    // Turning AI ON is always fine (that's the safe default).
    if (newMode === "live") {
      const unlocked = await gate.request("turn OFF the AI receptionist");
      if (!unlocked) {
        setMode(business.call_handler || "ai"); // revert visual state
        return;
      }
    }
    setBusy(true);
    try {
      await api.patch("/settings", { call_handler: newMode, forward_phone: newFwd });
      toast.success(newMode === "ai" ? "AI receptionist is answering calls" : "Calls will forward to a live person");
      refresh && refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  return (
    <div className="paper-card p-6">
      {gate.modal}
      <div className="overline mb-2">Who answers your phone right now?</div>
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => { setMode("ai"); save("ai", fwd); }}
          disabled={busy}
          className={`paper-card p-4 text-left border-2 transition-[transform,border-color] duration-200 ${mode === "ai" ? "border-[#1E3A2F] -translate-y-0.5" : "border-transparent hover:border-[#1E3A2F]/50"}`}
          data-testid="handler-ai"
        >
          <div className="font-heading font-extrabold text-lg">AI Receptionist</div>
          <p className="text-xs text-[#5C6B61] mt-1">BuzzerAI answers 24/7, takes names, quotes wait times, hands off for to-go.</p>
          {mode === "ai" && <span className="tag tag-seated mt-2 inline-block">Active</span>}
        </button>
        <button
          type="button"
          onClick={() => { setMode("live"); if (fwd) save("live", fwd); }}
          disabled={busy}
          className={`paper-card p-4 text-left border-2 transition-[transform,border-color] duration-200 ${mode === "live" ? "border-[#1E3A2F] -translate-y-0.5" : "border-transparent hover:border-[#1E3A2F]/50"}`}
          data-testid="handler-live"
        >
          <div className="font-heading font-extrabold text-lg">Live Staff</div>
          <p className="text-xs text-[#5C6B61] mt-1">Forward incoming calls to a phone number a person will pick up (host stand, GM cell, etc.).</p>
          {mode === "live" && <span className="tag tag-seated mt-2 inline-block">Active</span>}
        </button>
      </div>
      {mode === "live" && (
        <div className="mt-4 grid grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <label className="overline block mb-1">Forward calls to (E.164)</label>
            <input className="input-field" placeholder="+15555550100" value={fwd} onChange={(e) => setFwd(e.target.value)} data-testid="handler-forward" />
          </div>
          <button onClick={() => save("live", fwd)} disabled={busy || !fwd} className="btn-primary" data-testid="handler-save-forward">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      )}
      <p className="text-xs text-[#5C6B61] mt-3 leading-snug">Toggle any time — the change takes effect on the very next call. When switched to Live, callers hear a short greeting then the call rings your forward number. If nobody picks up in 20 seconds, they're told to try again or text.</p>
    </div>
  );
};

const ForwardingHelper = ({ business }) => {
  const [lookupPhone, setLookupPhone] = useState("");
  const [lookup, setLookup] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [device, setDevice] = useState("iphone"); // iphone | android | landline
  const landingNumber = business?.twilio_phone_number;

  // Digits only, no country code — used inside the GSM dial string **21*DIGITS#
  const landingDigits = (landingNumber || "").replace(/\D/g, "").replace(/^1/, "");
  const fullDialCode = lookup?.carrier_hint?.code && landingDigits
    ? (lookup.carrier_hint.code.endsWith("*")
        ? `${lookup.carrier_hint.code}${landingDigits}#`   // GSM style: **21*DIGITS#
        : `${lookup.carrier_hint.code}${landingDigits}`)   // CDMA style: *72DIGITS
    : lookup?.carrier_hint?.code;

  const detect = async () => {
    if (!lookupPhone.trim()) return toast.error("Enter your existing business phone number");
    setLookupBusy(true);
    try {
      const r = await api.post("/twilio/lookup", { phone_number: lookupPhone.trim() });
      setLookup(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connect your BuzzerAI answering line below first, then come back.");
    }
    setLookupBusy(false);
  };

  const iphoneWorks = lookup?.carrier_hint?.iphone_settings_works;
  const iphoneCaveat = lookup?.carrier_hint?.iphone_caveat;
  const disableCode = lookup?.carrier_hint?.disable_code;

  return (
    <div className="paper-card p-5 mb-4 bg-[#F7F6F2]" data-testid="voice-forwarding-helper">
      <div className="overline mb-1">Forward your existing number</div>
      <p className="text-sm text-[#5C6B61] mb-3">
        Keep the number your guests already know — no porting, no new numbers to promote. Enter it below and we'll show the exact steps for your device + carrier. Your line rings BuzzerAI; the AI takes over from there.
      </p>

      <div className="paper-card p-3 mb-3 flex flex-col sm:flex-row gap-2 sm:items-center">
        <input className="input-field flex-1" placeholder="+15555550100 — your current business line" value={lookupPhone} onChange={(e) => setLookupPhone(e.target.value)} data-testid="forward-detect-input" />
        <button onClick={detect} disabled={lookupBusy} className="btn-primary" data-testid="forward-detect-btn">
          {lookupBusy ? "Checking…" : "Show forwarding steps"}
        </button>
      </div>

      {lookup && lookup.carrier_hint && (
        <div className="paper-card p-4 bg-white border border-[#1E3A2F]/20" data-testid="forward-carrier-hint">
          <div className="text-xs text-[#5C6B61] mb-3">
            Detected {lookup.national_format} · <b className="text-[#121914] capitalize">{lookup.line_type}</b>{lookup.carrier ? ` on ${lookup.carrier}` : ""}
          </div>

          {/* Device picker */}
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-wide text-[#5C6B61] mb-1.5">What's the house phone?</div>
            <div className="inline-flex rounded-md border border-[#E1DFD5] overflow-hidden">
              {[
                { key: "iphone", label: "iPhone" },
                { key: "android", label: "Android" },
                { key: "landline", label: "Dial-code / other" },
              ].map((d) => (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => setDevice(d.key)}
                  className={`text-xs px-3 py-1.5 ${device === d.key ? "bg-[#1E3A2F] text-white" : "bg-white text-[#121914] hover:bg-[#F7F6F2]"}`}
                  data-testid={`forward-device-${d.key}`}
                >{d.label}</button>
              ))}
            </div>
          </div>

          {/* iPhone-first path — the primary way, since most host stands run on iPhones */}
          {device === "iphone" && iphoneWorks && landingNumber && (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wide text-[#5C6B61] mb-1">1 · On the iPhone (works on {lookup.carrier_hint.label})</div>
              <ol className="text-sm text-[#121914] leading-snug list-decimal ml-5 space-y-1">
                <li>Open <b>Settings</b></li>
                <li>Tap <b>Phone</b> → <b>Call Forwarding</b></li>
                <li>Toggle <b>Call Forwarding ON</b></li>
                <li>Tap <b>Forward To</b> and enter <span className="font-mono">{landingNumber}</span></li>
                <li>Back out — a small forwarding arrow appears in the status bar</li>
              </ol>
            </div>
          )}

          {device === "iphone" && !iphoneWorks && (
            <div className="mb-3 p-3 rounded bg-[#FEF3C7] border border-[#F59E0B]/40" data-testid="forward-iphone-caveat">
              <div className="text-[10px] uppercase tracking-wide text-[#92400E] mb-1">⚠️ iPhone note for {lookup.carrier_hint.label}</div>
              <p className="text-sm text-[#78350F] leading-snug">{iphoneCaveat || "Your carrier doesn't expose the iPhone Settings → Phone → Call Forwarding toggle. Use the dial-code path below instead."}</p>
            </div>
          )}

          {/* Android has the same Settings path across manufacturers */}
          {device === "android" && landingNumber && (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wide text-[#5C6B61] mb-1">1 · On the Android phone</div>
              <ol className="text-sm text-[#121914] leading-snug list-decimal ml-5 space-y-1">
                <li>Open the <b>Phone</b> app</li>
                <li>Tap the three-dot menu → <b>Settings</b> → <b>Calls</b> → <b>Call forwarding</b></li>
                <li>Tap <b>Always forward</b> and enter <span className="font-mono">{landingNumber}</span></li>
                <li>Tap <b>Enable</b> / <b>Turn on</b></li>
              </ol>
              <p className="text-[11px] text-[#5C6B61] mt-1">Menu names vary slightly by manufacturer (Samsung, Pixel, OnePlus) but the path is always Phone app → Settings → Call forwarding.</p>
            </div>
          )}

          {/* Dial code — shown for landline/other, or as a fallback for iPhone/Android */}
          {(device === "landline" || !iphoneWorks || device === "android") && fullDialCode && (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wide text-[#5C6B61]">
                {device === "iphone" || device === "android" ? "Backup — dial-code method" : `1 · Dial from your existing line (${lookup.carrier_hint.label})`}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <div className="font-mono text-lg text-[#1E3A2F]" data-testid="forward-code">{fullDialCode}</div>
                <button type="button" onClick={() => { navigator.clipboard.writeText(fullDialCode); toast.success(`Copied ${fullDialCode}`); }} className="btn-ghost !py-1 !px-2 text-[10px]" data-testid="forward-code-copy">Copy code</button>
              </div>
              <p className="text-sm text-[#121914] leading-snug mt-1">{lookup.carrier_hint.instructions}</p>
              {!landingDigits && (
                <p className="text-[11px] text-[#A5382A] mt-1">Connect your BuzzerAI answering line below first — we need it to build the full dial code.</p>
              )}
            </div>
          )}

          {/* Landing number — always show so it can be copied */}
          <div className="p-3 rounded bg-[#C2D1C1]/30 border border-[#1E3A2F]/10">
            <div className="text-[10px] uppercase tracking-wide text-[#5C6B61]">Forward TO your BuzzerAI answering line</div>
            {landingNumber ? (
              <div className="flex items-center gap-2 mt-1">
                <div className="font-mono text-lg text-[#1E3A2F]" data-testid="forward-target">{landingNumber}</div>
                <button type="button" onClick={() => { navigator.clipboard.writeText(landingNumber); toast.success(`Copied ${landingNumber}`); }} className="btn-ghost !py-1 !px-2 text-[10px]" data-testid="forward-target-copy">Copy number</button>
              </div>
            ) : (
              <p className="text-xs text-[#5C6B61] mt-1">Connect your BuzzerAI answering line below first — that becomes the number you forward to.</p>
            )}
          </div>

          {/* Disable code — how to turn it OFF later */}
          {disableCode && (
            <div className="mt-3 flex items-center gap-2 text-xs text-[#5C6B61]">
              <span>To turn forwarding OFF later, dial</span>
              <span className="font-mono text-[#1E3A2F]">{disableCode}</span>
              <span>from the same line.</span>
            </div>
          )}

          <p className="text-[11px] text-[#5C6B61] leading-snug mt-3">
            After forwarding is on, dial your original business number from any phone — you should hear the AI greeting within one ring. Call log below shows every call as it lands.
          </p>
        </div>
      )}
    </div>
  );
};

const TwilioWizard = ({ business, refresh }) => {
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(1); // 1 = enter creds, 2 = verified pick number, 3 = wired up
  const [creds, setCreds] = useState({
    account_sid: business.twilio_account_sid || "",
    auth_token: business.twilio_auth_token || "",
  });
  const [numbers, setNumbers] = useState([]);
  const [chosenNumber, setChosenNumber] = useState(business.twilio_phone_number || "");
  const [testTo, setTestTo] = useState("");
  const [callTo, setCallTo] = useState(business.owner_phone || "");
  const [callBusy, setCallBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/twilio/status"); setStatus(r.data); if (r.data.configured) setStep(3); }
    catch (err) { console.error("Twilio status load failed:", err); }
  };
  useEffect(() => {
    load();
    // Poll every 15s while wizard is open — flips "Not connected" → "Receiving calls" the moment a real call lands
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async () => {
    // Strip whitespace/newlines that sneak in when pasting from the Twilio console.
    const cleaned = {
      account_sid: (creds.account_sid || "").trim(),
      auth_token: (creds.auth_token || "").trim(),
    };
    if (!cleaned.account_sid || !cleaned.auth_token) return toast.error("Paste both SID and Auth Token first");
    // Reflect the cleaned values back into the inputs so the user sees exactly what we're sending.
    setCreds(cleaned);
    setBusy(true);
    try {
      const r = await api.post("/twilio/verify", cleaned);
      setNumbers(r.data.numbers || []);
      if (r.data.numbers.length === 0) { toast.error("Verified — but no numbers on this account. Buy one in the Twilio console."); setBusy(false); return; }
      toast.success(`Verified · ${r.data.numbers.length} number(s) available`);
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
    setBusy(false);
  };

  const saveNumber = async () => {
    if (!chosenNumber) return toast.error("Pick a phone number");
    setBusy(true);
    try {
      await api.patch("/settings", {
        twilio_account_sid: creds.account_sid,
        twilio_auth_token: creds.auth_token,
        twilio_phone_number: chosenNumber,
      });
      toast.success("Credentials saved. Wiring the webhooks…");
      // Automatically configure Twilio webhooks so the operator doesn't need to
      // click through the Twilio console. On failure, we leave a persistent red
      // banner up top (see WebhookStatusBanner) so it can't be missed by a
      // dismissed toast.
      try {
        await api.post("/twilio/auto-configure", {});
        toast.success("Twilio webhooks wired — you're live!");
        setStep(3);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Number saved, but auto-wire failed. See the red banner up top — one click retries it.");
        setStep(3);
      }
      await load();
      refresh && refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const autoConfigure = async () => {
    setBusy(true);
    try {
      const r = await api.post("/twilio/auto-configure", {});
      toast.success(`Webhooks pointed at BuzzerAI — ${r.data.phone_number} is live`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-wire failed");
    }
    setBusy(false);
  };

  const sendTest = async () => {
    if (!testTo) return toast.error("Add a phone number to text");
    try {
      const r = await api.post("/twilio/test-sms", { to: testTo });
      if (r.data.mocked) toast.error("Twilio still in mocked mode — check saved creds");
      else if (r.data.status === "sent") toast.success(`Sent — SID ${r.data.sid.slice(0, 10)}…`);
      else toast.error(r.data.error || "Send failed");
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  const callSelf = async () => {
    if (!callTo || callTo.length < 4) return toast.error("Enter your mobile number to ring");
    setCallBusy(true);
    try {
      const r = await api.post("/twilio/test-call", { to: callTo });
      toast.success(r.data.message || `Ringing ${callTo}…`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test call failed");
    }
    setCallBusy(false);
  };

  const disconnect = async () => {
    if (!confirm("Remove saved Twilio creds? SMS and Voice will stop working until you reconnect.")) return;
    await api.patch("/settings", { twilio_account_sid: "", twilio_auth_token: "", twilio_phone_number: "" });
    setStep(1); setCreds({ account_sid: "", auth_token: "" }); setChosenNumber(""); setNumbers([]);
    await load(); refresh && refresh();
    toast.success("Disconnected");
  };

  return (
    <div className="paper-card p-6">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Phone size={22} color="#1E3A2F" weight="fill" />
        <div className="overline">Your BuzzerAI answering line</div>
        {status?.receiving_calls ? (
          <span className="tag tag-seated inline-flex items-center gap-1" data-testid="twilio-chip-receiving"><CheckCircle size={12} /> Receiving calls</span>
        ) : status?.configured ? (
          <span className="tag tag-notified inline-flex items-center gap-1" data-testid="twilio-chip-live"><CheckCircle size={12} /> Live · waiting for first call</span>
        ) : (
          <span className="tag tag-noshow inline-flex items-center gap-1" data-testid="twilio-chip-not-connected"><Warning size={12} /> Not connected</span>
        )}
        {status?.balance_dollars != null && (
          <a href="https://console.twilio.com/us1/billing/manage-billing/billing-overview" target="_blank" rel="noreferrer"
             className={`tag inline-flex items-center gap-1 ${status.low_balance ? "tag-noshow" : "tag-seated"}`}
             data-testid="twilio-balance-chip">
            {status.low_balance && <Warning size={12} />} ${status.balance_dollars.toFixed(2)} in Twilio{status.low_balance ? " — top up" : ""}
          </a>
        )}
        {status?.sms_failing && (
          <a href="https://console.twilio.com/us1/monitor/logs/sms" target="_blank" rel="noreferrer"
             className="tag tag-noshow inline-flex items-center gap-1" data-testid="twilio-sms-failing-chip">
            <Warning size={12} /> {status.sms_errors_last_hour} SMS failures / hr
          </a>
        )}
      </div>

      <p className="text-xs text-[#5C6B61] mb-3 leading-snug">
        This is the number your existing business line forwards TO. BuzzerAI picks up here, the AI answers, and callers never know they were rerouted. Grab a $1.15/mo Twilio number for this — you'll only ever share the number above with your guests.
      </p>
      <ol className="text-xs text-[#5C6B61] mb-4 space-y-1">
        <li>1. Paste your Account SID + Auth Token below and click Verify</li>
        <li>2. Pick the number to use and click Save — we'll auto-wire the webhooks for you.</li>
      </ol>

      {step < 3 && (
        <div className="space-y-3">
          <div>
            <label className="overline block mb-1">Account SID</label>
            <input className="input-field font-mono text-xs" placeholder="AC…" value={creds.account_sid} onChange={(e) => setCreds({ ...creds, account_sid: e.target.value })} data-testid="twilio-sid" />
          </div>
          <div>
            <label className="overline block mb-1">Auth Token</label>
            <input className="input-field font-mono text-xs" type="password" placeholder="…" value={creds.auth_token} onChange={(e) => setCreds({ ...creds, auth_token: e.target.value })} data-testid="twilio-token" />
            <p className="text-xs text-[#5C6B61] mt-1">Grab both from <a className="underline" href="https://console.twilio.com/" target="_blank" rel="noreferrer">console.twilio.com</a> home page.</p>
          </div>
          <button onClick={verify} disabled={busy} className="btn-primary w-full justify-center" data-testid="twilio-verify">
            {busy ? "Verifying…" : (<><LinkSimple size={16} color="#fff" /> Verify credentials</>)}
          </button>

          {step === 2 && numbers.length > 0 && (
            <div className="border-t border-[#E1DFD5] pt-4">
              <div className="overline mb-2">Pick your number</div>
              <div className="space-y-2 max-h-40 overflow-auto">
                {numbers.map((n) => (
                  <label key={n.phone_number} className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer ${chosenNumber === n.phone_number ? "border-[#1E3A2F]" : "border-[#E1DFD5]"}`}>
                    <input type="radio" name="tw-num" value={n.phone_number} checked={chosenNumber === n.phone_number} onChange={() => setChosenNumber(n.phone_number)} />
                    <div className="flex-1">
                      <div className="font-mono text-sm">{n.phone_number}</div>
                      {n.friendly_name && <div className="text-xs text-[#5C6B61]">{n.friendly_name}</div>}
                    </div>
                  </label>
                ))}
              </div>
              <button onClick={saveNumber} disabled={busy || !chosenNumber} className="btn-primary w-full justify-center mt-3" data-testid="twilio-save-number">
                {busy ? "Saving…" : "Save & activate"}
              </button>
            </div>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div className="paper-card p-4 bg-[#C2D1C1]/40">
            <div className="text-sm"><b>Active number:</b> <span className="font-mono">{status?.phone_number || "(env fallback)"}</span></div>
            <div className="text-xs text-[#5C6B61] mt-1">SMS + Voice will now use your Twilio account. Real messages, real minutes.</div>
          </div>

          <div className="border-t border-[#E1DFD5] pt-4">
            <div className="overline mb-2">Webhooks</div>
            <p className="text-xs text-[#5C6B61] mb-3">One click points your Twilio number at BuzzerAI's Voice + SMS endpoints. Safe to re-run any time (e.g. after redeploying or changing your domain).</p>
            <button onClick={autoConfigure} disabled={busy} className="btn-primary" data-testid="twilio-auto-configure">
              {busy ? "Wiring…" : "Auto-wire webhooks"}
            </button>
          </div>

          <div className="border-t border-[#E1DFD5] pt-4">
            <div className="overline mb-2">Call yourself — hear your AI greeting</div>
            <p className="text-xs text-[#5C6B61] mb-2 leading-snug">We'll ring your mobile and connect you to your live AI receptionist — the exact experience your guests would have.</p>
            <div className="flex items-center gap-2">
              <input className="input-field flex-1" placeholder="+15555550100 (your mobile)" value={callTo} onChange={(e) => setCallTo(e.target.value)} data-testid="twilio-call-to" />
              <button onClick={callSelf} disabled={callBusy} className="btn-primary" data-testid="twilio-call-self">
                <Phone size={14} color="#fff" weight="fill" /> {callBusy ? "Ringing…" : "Ring me"}
              </button>
            </div>
          </div>

          <div className="border-t border-[#E1DFD5] pt-4">
            <div className="overline mb-2">Send a test SMS</div>
            <div className="flex items-center gap-2">
              <input className="input-field flex-1" placeholder="+15555550100 (your mobile)" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="twilio-test-to" />
              <button onClick={sendTest} className="btn-primary" data-testid="twilio-test-send"><PaperPlaneTilt size={14} color="#fff" weight="fill" /> Send</button>
            </div>
          </div>

          <button onClick={disconnect} className="text-xs text-[#A5382A] hover:underline" data-testid="twilio-disconnect">Disconnect Twilio</button>
        </div>
      )}
    </div>
  );
};

const AIKnowledgePanel = ({ business, refresh }) => {
  const [editing, setEditing] = useState(null); // field key currently in edit mode
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [dismissing, setDismissing] = useState(null);

  const loadSuggestions = async () => {
    try {
      const r = await api.get("/ai-knowledge/suggestions");
      setSuggestions(r.data.suggestions || []);
    } catch (err) { console.debug("ai suggestions poll failed:", err?.message); }
  };
  useEffect(() => {
    loadSuggestions();
    const t = setInterval(loadSuggestions, 60000);
    return () => clearInterval(t);
  }, []);

  const dismiss = async (q) => {
    setDismissing(q);
    try {
      await api.post("/ai-knowledge/dismiss", { question: q });
      toast.success("Marked as answered. It won't show up here again.");
      await loadSuggestions();
    } catch (e) { toast.error("Dismiss failed"); }
    setDismissing(null);
  };

  const FIELDS = [
    { key: "business_name", label: "Business name", placeholder: "Mario's Trattoria", hint: "The AI uses this in every greeting and the Google fallback." },
    { key: "hours", label: "Hours", placeholder: "Tue–Sun 5pm–10pm, closed Mondays", hint: "AI recites this verbatim for hours questions." },
    { key: "address", label: "Address", placeholder: "123 Main St, San Francisco, CA 94103", hint: "AI recites this for 'where are you' questions." },
    { key: "twilio_phone_number", label: "Business phone (Twilio)", placeholder: "+15555550100", readonly: true, hint: "The number callers dial. Change in the Twilio wizard above." },
    { key: "forward_phone", label: "Live-transfer phone", placeholder: "+15555550100", hint: "Where the AI Dials when a caller asks for a live person or wants to place a to-go order (if no to-go link is set)." },
    { key: "owner_phone", label: "Owner mobile", placeholder: "+15555550100", hint: "Fallback for the fix-it-now test call and if forward phone is empty." },
    { key: "togo_url", label: "To-go ordering URL", placeholder: "https://order.mariostrattoria.com", hint: "If set, the AI will text this link to callers asking for to-go — no live transfer needed." },
    { key: "ai_greeting", label: "AI greeting", placeholder: `Hello, thanks for calling ${business?.business_name || "us"}. How can I help?`, hint: "The exact first sentence callers hear." },
  ];

  const start = (k) => { setValues({ [k]: business?.[k] || "" }); setEditing(k); };
  const cancel = () => { setEditing(null); setValues({}); };
  const save = async (k) => {
    setSaving(true);
    try {
      await api.patch("/settings", { [k]: values[k] });
      toast.success(`${k.replace(/_/g, " ")} updated`);
      setEditing(null);
      refresh && (await refresh());
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  // Which live-phone the AI will actually Dial when it transfers.
  const activeDialTarget = business?.forward_phone || business?.owner_phone || "(none — will fall back to SMS callback)";

  // Completeness meter — nudges operators to fill missing facts, which directly
  // reduces "unknown fact" fallbacks and callback tasks.
  const requiredKeys = ["business_name", "hours", "address", "forward_phone", "ai_greeting"];
  const filledCount = requiredKeys.filter((k) => (business?.[k] || "").toString().trim()).length;
  const totalCount = requiredKeys.length;
  const pct = Math.round(100 * filledCount / totalCount);

  return (
    <div className="paper-card p-5 mb-6" data-testid="ai-knowledge-panel">
      <div className="flex items-start justify-between mb-2 gap-4">
        <div>
          <div className="overline">What your AI knows</div>
          <div className="font-heading font-extrabold text-base text-[#121914]">{filledCount}/{totalCount} core facts filled · your AI handles ~{Math.round(pct * 0.85 + 15)}% inline</div>
          <div className="w-40 bg-[#E1DFD5] h-1.5 rounded-full mt-2 overflow-hidden">
            <div className={`h-full transition-all duration-500 ${pct >= 80 ? "bg-[#1E3A2F]" : pct >= 50 ? "bg-[#F5C543]" : "bg-[#A5382A]"}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="text-right text-xs text-[#5C6B61] leading-snug hidden sm:block">
          <div>Live-transfer target</div>
          <div className="font-mono text-[#1E3A2F]" data-testid="ai-dial-target">{activeDialTarget}</div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        {FIELDS.map((f) => {
          const val = business?.[f.key];
          const filled = !!(val && String(val).trim());
          const isEditing = editing === f.key;
          return (
            <div key={f.key} className={`paper-card p-3 ${filled ? "" : "border border-dashed border-[#F59E0B]/50 bg-[#FEF3C7]/30"}`} data-testid={`ai-fact-${f.key}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="overline">{f.label}</div>
                {!isEditing && !f.readonly && (
                  <button onClick={() => start(f.key)} className="text-[10px] font-semibold text-[#1E3A2F] hover:underline uppercase tracking-wide" data-testid={`ai-fact-edit-${f.key}`}>{filled ? "Edit" : "Add"}</button>
                )}
                {f.readonly && <span className="text-[10px] text-[#5C6B61] uppercase tracking-wide">Read-only</span>}
              </div>
              {isEditing ? (
                <div className="space-y-2">
                  <input
                    className="input-field text-sm"
                    autoFocus
                    value={values[f.key] || ""}
                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    data-testid={`ai-fact-input-${f.key}`}
                  />
                  <div className="flex gap-2 justify-end">
                    <button onClick={cancel} disabled={saving} className="btn-ghost !py-1 !px-2 text-xs">Cancel</button>
                    <button onClick={() => save(f.key)} disabled={saving} className="btn-primary !py-1 !px-3 text-xs" data-testid={`ai-fact-save-${f.key}`}>{saving ? "Saving…" : "Save"}</button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className={`text-sm ${filled ? "text-[#121914]" : "text-[#A5382A] italic"} break-words`} data-testid={`ai-fact-value-${f.key}`}>
                    {filled ? String(val) : "Not set — the AI will refer callers to your Google Business Profile instead."}
                  </div>
                  <p className="text-[11px] text-[#5C6B61] mt-1 leading-snug">{f.hint}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {suggestions.length > 0 && (
        <div className="mt-5 pt-5 border-t border-[#E1DFD5]" data-testid="ai-suggestions">
          <div className="overline mb-1">Questions your AI couldn't answer</div>
          <p className="text-xs text-[#5C6B61] mb-3 leading-snug">
            Real caller questions that hit the "check our Google Business Profile" fallback. Add the answer as a fact above so future callers get it inline — or dismiss to hide.
          </p>
          <div className="space-y-2">
            {suggestions.slice(0, 5).map((s) => (
              <div key={s.question} className="paper-card p-3 flex items-start gap-3 bg-[#FEF3C7]/40" data-testid="ai-suggestion-row">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#121914] leading-snug">"{s.question}"</div>
                  <div className="text-[11px] text-[#5C6B61] mt-0.5">
                    {s.count}× asked · last: {new Date(s.last_seen).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </div>
                </div>
                <button
                  onClick={() => dismiss(s.question.toLowerCase().trim())}
                  disabled={dismissing === s.question.toLowerCase().trim()}
                  className="btn-ghost !py-1 !px-2 text-[10px] flex-shrink-0"
                  data-testid="ai-suggestion-dismiss"
                >{dismissing === s.question.toLowerCase().trim() ? "…" : "Mark answered"}</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CallbackTasksPanel = () => {
  const [tasks, setTasks] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/callback-tasks");
      setTasks(r.data.tasks || []);
    } catch (err) { console.debug("callback-tasks poll failed:", err?.message); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const complete = async (id) => {
    setBusy(id);
    try {
      await api.post(`/callback-tasks/${id}/complete`, {});
      toast.success("Marked done");
      await load();
    } catch (e) { toast.error("Complete failed"); }
    setBusy(null);
  };

  if (tasks.length === 0) return null;
  return (
    <div className="paper-card p-5 mb-6 border-l-4 border-[#F5C543]" data-testid="callback-tasks-panel">
      <div className="mb-3">
        <div className="overline">Follow-ups needed</div>
        <div className="font-heading font-extrabold text-base text-[#121914]">{tasks.length} caller{tasks.length === 1 ? "" : "s"} to call back</div>
        <p className="text-xs text-[#5C6B61] mt-1 leading-snug">Callers whose questions the AI couldn't fully resolve. Call them back and mark done.</p>
      </div>
      <div className="space-y-2">
        {tasks.slice(0, 10).map((t) => (
          <div key={t.id} className="paper-card p-3 flex items-start gap-3 bg-[#FEF3C7]/40" data-testid={`callback-task-${t.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-[#121914] font-mono">{t.phone || "(no number)"}</div>
              <div className="text-xs text-[#5C6B61] leading-snug mt-0.5">
                {t.reason} · {t.question ? `asked: "${t.question}"` : ""} · {new Date(t.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </div>
            </div>
            <a href={`tel:${t.phone}`} className="btn-ghost !py-1 !px-2 text-[10px]" data-testid={`callback-dial-${t.id}`}>Call</a>
            <button onClick={() => complete(t.id)} disabled={busy === t.id} className="btn-primary !py-1 !px-2 text-[10px]" data-testid={`callback-done-${t.id}`}>{busy === t.id ? "…" : "Done"}</button>
          </div>
        ))}
      </div>
    </div>
  );
};

const SmartAddQueuePanel = () => {
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/smart-adds");
      // Existing endpoint returns a plain list of docs.
      setQueue(Array.isArray(r.data) ? r.data : (r.data.queue || []));
    } catch (err) { console.debug("smart-adds poll failed:", err?.message); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const act = async (id, path, verb) => {
    setBusy(`${id}-${verb}`);
    try {
      if (verb === "cancel") {
        await api.delete(`/smart-adds/${id}`);
      } else {
        await api.post(`/smart-adds/${id}/${path}`, {});
      }
      toast.success(verb === "promote" ? "Added to the live waitlist" : "Cancelled");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    setBusy(null);
  };

  if (queue.length === 0) return null;
  return (
    <div className="paper-card p-5 mb-6 border-l-4 border-[#1E3A2F]" data-testid="smart-add-queue">
      <div className="mb-3">
        <div className="overline">Wait Watch queue · same-day</div>
        <div className="font-heading font-extrabold text-base text-[#121914]">{queue.length} caller{queue.length === 1 ? "" : "s"} waiting to be auto-added</div>
        <p className="text-xs text-[#5C6B61] mt-1 leading-snug">
          The AI captured these by phone. We watch the wait and add them to the live waitlist at the right time. Promote now if a table just opened up, or cancel if they no-show.
        </p>
      </div>
      <div className="space-y-2">
        {queue.map((s) => {
          const target = s.desired_eat_time ? new Date(s.desired_eat_time) : null;
          const targetLabel = target ? target.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "(unknown)";
          const stateColor = s.status === "promoted" ? "bg-[#C2D1C1] text-[#1E3A2F]" : s.status === "reminded" ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#E6E2D6] text-[#5C6B61]";
          return (
            <div key={s.id} className="paper-card p-3 grid grid-cols-[1fr_auto] gap-3 items-center" data-testid={`sa-row-${s.id}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-2">
                  <div className="font-heading font-extrabold text-sm text-[#121914]">{s.name}</div>
                  <div className="text-xs text-[#5C6B61]">· party of {s.party_size}</div>
                  <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${stateColor}`}>{s.status}</span>
                </div>
                <div className="text-xs text-[#5C6B61] leading-snug mt-0.5">
                  Target: <b className="text-[#1E3A2F]">{targetLabel}</b>{s.raw_time_input ? ` ("${s.raw_time_input}")` : ""} · {s.phone}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                {(s.status === "scheduled" || s.status === "reminded") && (
                  <>
                    <button
                      onClick={() => act(s.id, "promote", "promote")}
                      disabled={busy === `${s.id}-promote`}
                      className="btn-primary !py-1 !px-2 text-[10px]"
                      data-testid={`sa-promote-${s.id}`}
                    >{busy === `${s.id}-promote` ? "…" : "Add now"}</button>
                    <button
                      onClick={() => act(s.id, "cancel", "cancel")}
                      disabled={busy === `${s.id}-cancel`}
                      className="btn-ghost !py-1 !px-2 text-[10px]"
                      data-testid={`sa-cancel-${s.id}`}
                    >{busy === `${s.id}-cancel` ? "…" : "Cancel"}</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function DashVoice() {
  const { business, refresh } = useAuth();
  const ent = useEntitlements();
  const entitled = ent?.entitled?.ai_voice;
  const [calls, setCalls] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);
  const [voiceErrors, setVoiceErrors] = useState([]);
  const [selfTest, setSelfTest] = useState(null);
  const [selfTestBusy, setSelfTestBusy] = useState(false);

  useEffect(() => {
    if (entitled && !hasSeenVoiceTour()) setTourOpen(true);
  }, [entitled]);

  const load = async () => {
    if (!entitled) return;
    try { const r = await api.get("/calls"); setCalls(r.data); }
    catch (err) { console.error("Failed to load calls:", err); }
    // Also poll voice errors — small collection, cheap query.
    try { const er = await api.get("/twilio/voice-errors"); setVoiceErrors(er.data.errors || []); }
    catch (err) { console.debug("voice-errors poll failed:", err?.message); }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [entitled]);

  // Auto-run a lightweight self-test once when the operator lands on this page
  // AND the setup is complete. Silent in the background — surfaces a red
  // banner only if the pipeline is broken.
  useEffect(() => {
    if (!entitled || !business?.twilio_phone_number) return;
    api.post("/twilio/self-test", {}).then((r) => setSelfTest(r.data)).catch((err) => console.debug("self-test failed:", err?.message));
  }, [entitled, business?.twilio_phone_number]);

  const runSelfTest = async () => {
    setSelfTestBusy(true);
    try {
      const r = await api.post("/twilio/self-test", {});
      setSelfTest(r.data);
      if (r.data.ok) toast.success(r.data.message);
      else toast.error(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Self-test failed"); }
    setSelfTestBusy(false);
  };

  if (!entitled) return <LockedFeature addonKey="ai_voice" name="AI Voice Receptionist" description="Turn on the Voice add-on to point a Twilio number at BuzzerAI. The AI answers calls, adds guests to the waitlist, and hands off to a person. $12/month, cancel anytime." />;

  const voiceWebhook = `${BACKEND_URL}/api/twilio/voice/${business.slug}`;
  const smsWebhook = `${BACKEND_URL}/api/twilio/inbound`;
  const copy = (v, l) => { navigator.clipboard.writeText(v); toast.success(`${l} copied`); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline">AI Reception</div>
          <h1 className="text-3xl">Voice receptionist</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/help/twilio-setup" className="btn-ghost !py-2 !px-3 text-sm hidden sm:inline-flex items-center gap-1.5" data-testid="voice-open-playbook">
            <LinkSimple size={14} color="#1E3A2F" /> Twilio playbook
          </Link>
          <Link to="/dashboard/a2p" className="btn-ghost !py-2 !px-3 text-sm hidden sm:inline-flex items-center gap-1.5" data-testid="voice-open-a2p">
            <PaperPlaneTilt size={14} color="#1E3A2F" /> SMS Compliance
          </Link>
          <button
            type="button"
            onClick={() => setTourOpen(true)}
            className="btn-ghost !py-2 !px-3 text-sm inline-flex items-center gap-1.5"
            data-testid="voice-tour-launch"
          >
            <Sparkle size={14} color="#1E3A2F" weight="fill" /> Take a tour
          </button>
        </div>
      </div>
      <VoiceTour open={tourOpen} onClose={() => setTourOpen(false)} />

      <WebhookStatusBanner business={business} refresh={refresh} />

      <AIKnowledgePanel business={business} refresh={refresh} />

      <CallbackTasksPanel />

      <SmartAddQueuePanel style={{ display: "none" }} />

      {selfTest && !selfTest.ok && (
        <div className="paper-card p-4 mb-6 border-2 border-[#A5382A] bg-[#FEF2F0]" data-testid="selftest-banner">
          <div className="flex items-start gap-3">
            <Warning size={22} color="#A5382A" weight="fill" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-extrabold text-[#A5382A] text-base leading-snug">Voice pipeline unhealthy</div>
              <p className="text-sm text-[#7A1F16] leading-snug mt-1">
                {selfTest.message} · Greeting: {selfTest.results?.greeting?.elapsed_s ?? "?"}s ({selfTest.results?.greeting?.ok ? "OK" : "FAIL"}) · AI response: {selfTest.results?.handle?.elapsed_s ?? "?"}s ({selfTest.results?.handle?.ok ? "OK" : "FAIL"})
              </p>
              {(selfTest.results?.greeting?.error || selfTest.results?.handle?.error || selfTest.results?.greeting?.status_code >= 400 || selfTest.results?.handle?.status_code >= 400) && (
                <div className="mt-2 text-[11px] text-[#7A1F16] bg-white/60 border border-[#A5382A]/30 rounded px-2 py-1.5 font-mono leading-snug break-words" data-testid="selftest-error-detail">
                  {selfTest.results?.greeting?.error && <div>greeting error: {selfTest.results.greeting.error}</div>}
                  {selfTest.results?.greeting?.status_code >= 400 && <div>greeting http {selfTest.results.greeting.status_code}: {selfTest.results.greeting.twiml_preview}</div>}
                  {selfTest.results?.handle?.error && <div>handle error: {selfTest.results.handle.error}</div>}
                  {selfTest.results?.handle?.status_code >= 400 && <div>handle http {selfTest.results.handle.status_code}: {selfTest.results.handle.twiml_preview}</div>}
                </div>
              )}
            </div>
            <button onClick={runSelfTest} disabled={selfTestBusy} className="btn-primary flex-shrink-0" data-testid="selftest-retry">
              {selfTestBusy ? "Testing…" : "Re-test"}
            </button>
          </div>
        </div>
      )}

      {voiceErrors.length > 0 && (
        <div className="paper-card p-5 mb-6 border-l-4 border-[#D96C5B]" data-testid="voice-errors-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline">Recent voice errors</div>
              <div className="font-heading font-extrabold text-base text-[#121914]">Last {Math.min(voiceErrors.length, 5)} of {voiceErrors.length} — check what broke</div>
            </div>
            <button onClick={runSelfTest} disabled={selfTestBusy} className="btn-ghost text-xs" data-testid="voice-errors-selftest">
              {selfTestBusy ? "Testing…" : "Run self-test"}
            </button>
          </div>
          <div className="space-y-2">
            {voiceErrors.slice(0, 5).map((e) => (
              <div key={e.id} className="text-xs text-[#5C6B61] grid grid-cols-[auto_auto_1fr] gap-3 border-b border-[#E1DFD5] pb-2 last:border-0 last:pb-0" data-testid={`voice-error-${e.id}`}>
                <span className="font-mono text-[#121914]">{formatTime(e.created_at)}</span>
                <span className="uppercase tracking-wide text-[10px] font-semibold bg-[#FEF2F0] text-[#A5382A] px-1.5 rounded self-center">{e.endpoint}</span>
                <span className="truncate">{e.call_sid ? <span className="font-mono">[{e.call_sid.slice(0, 12)}…] </span> : ""}{e.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <div className="md:col-span-3">
          <CallHandlerToggle business={business} refresh={refresh} />
        </div>
        <div className="md:col-span-3">
          <ForwardingHelper business={business} />
        </div>
        <div className="md:col-span-1 space-y-6">
          <TwilioWizard business={business} refresh={refresh} />

          <div className="paper-card p-6">
            <div className="overline mb-2">Webhook URLs (paste into Twilio)</div>
            <div className="space-y-3">
              <div>
                <div className="text-xs text-[#5C6B61] mb-1">Voice webhook (POST)</div>
                <div className="flex items-center gap-2">
                  <code className="text-[10px] bg-[#E6E2D6] px-2 py-2 rounded flex-1 truncate">{voiceWebhook}</code>
                  <button onClick={() => copy(voiceWebhook, "Voice URL")} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid="voice-copy"><Copy size={12} /></button>
                </div>
              </div>
              <div>
                <div className="text-xs text-[#5C6B61] mb-1">SMS webhook (POST)</div>
                <div className="flex items-center gap-2">
                  <code className="text-[10px] bg-[#E6E2D6] px-2 py-2 rounded flex-1 truncate">{smsWebhook}</code>
                  <button onClick={() => copy(smsWebhook, "SMS URL")} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid="sms-copy"><Copy size={12} /></button>
                </div>
              </div>
            </div>
          </div>

          <div className="paper-card p-6 bg-[#C2D1C1]/30">
            <div className="overline mb-2">What the AI can do inside a call</div>
            <ul className="text-sm text-[#5C6B61] space-y-2">
              <li>· <b className="text-[#121914]">add_to_waitlist</b> — takes name + party size, adds automatically</li>
              <li>· <b className="text-[#121914]">notify_guest</b> — texts a waiting guest that their table is ready</li>
              <li>· <b className="text-[#121914]">check_wait_time</b> — quotes the current wait</li>
              <li>· <b className="text-[#121914]">transfer_to_person</b> — logs a callback for to-go or special requests</li>
            </ul>
            <p className="text-xs text-[#5C6B61] mt-3 italic">Function-calling requires Realtime mode + OPENAI_API_KEY. Turn-based mode still handles waitlist joins and handoffs via prompt patterns.</p>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="paper-card overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-3 border-b border-[#E1DFD5] overline">
              <div className="col-span-3">Time</div>
              <div className="col-span-3">From</div>
              <div className="col-span-3">Status</div>
              <div className="col-span-3 text-right">Turns</div>
            </div>
            {calls.length === 0 && <div className="p-10 text-center text-[#5C6B61]">No calls yet. Wire up Twilio on the left to receive them here.</div>}
            <div className="max-h-[600px] overflow-y-auto" data-testid="call-log-scroll">
            {calls.map((c) => (
              <div key={c.call_sid} className="border-b border-[#E1DFD5] last:border-0">
                <button onClick={() => setExpanded(expanded === c.call_sid ? null : c.call_sid)} className="w-full grid grid-cols-12 items-center px-6 py-4 hover:bg-[#F7F6F2] text-left" data-testid={`call-row-${c.call_sid}`}>
                  <div className="col-span-3 text-sm">{formatTime(c.started_at)}</div>
                  <div className="col-span-3 text-sm">{c.from_number || "—"}</div>
                  <div className="col-span-3"><span className={`tag ${c.needs_handoff ? "tag-noshow" : c.status === "completed" ? "tag-seated" : "tag-waiting"}`}>{c.needs_handoff ? "handoff" : c.status}</span></div>
                  <div className="col-span-3 text-right text-sm text-[#5C6B61]">{(c.turns || []).length} <ArrowRight size={12} className="inline" /></div>
                </button>
                {expanded === c.call_sid && (
                  <div className="px-6 py-4 bg-[#F7F6F2] border-t border-[#E1DFD5]">
                    {c.voicemail_url && (
                      <div className="mb-4 p-4 rounded-lg bg-[#1E3A2F] text-[#F7F6F2]" data-testid={`voicemail-${c.call_sid}`}>
                        <div className="overline text-[#D96C5B] mb-2">Voicemail · {c.voicemail_duration || "?"}s</div>
                        <p className="text-xs text-[#E1DFD5] mb-3">Recorded {c.voicemail_received_at ? formatTime(c.voicemail_received_at) : ""}. Requires your Twilio account login to play.</p>
                        <a
                          href={c.voicemail_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 rounded-full bg-[#D96C5B] text-white px-3 py-1.5 text-xs font-semibold hover:bg-[#c25b4c]"
                          data-testid={`voicemail-listen-${c.call_sid}`}
                        >
                          Listen on Twilio →
                        </a>
                      </div>
                    )}
                    <div className="space-y-2 max-h-[320px] overflow-y-auto pr-2" data-testid={`call-transcript-${c.call_sid}`}>
                      {(c.turns || []).map((t, i) => (
                        <div key={`${c.call_sid}-${t.ts || i}`} className={`text-sm ${t.role === "user" ? "" : t.role === "system" ? "text-[#D96C5B] italic" : "text-[#1E3A2F]"}`}>
                          <span className="overline mr-2">{t.role === "user" ? "Caller" : t.role === "system" ? "Tool" : "AI"}</span>
                          {t.content}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
